import type { SipPlanStatus } from '../../../api/common.types'
import { isPlanDropDelayed, usePlanCountdown } from '../../../../lumpsum/plans-list/timer/shared/usePlanCountdown'
import { SIP_YOUR_PLAN_FEATURE_ROWS } from '../shared/sipYourPlanFeatureRows'
import {
  TimerPanelCountdownSection,
  TimerPanelFeatureRows,
  TimerPanelFooterCopy,
  TimerPanelFooterHeading,
  TimerPanelShell,
  TimerPanelStrategySection
} from '../../../../lumpsum/plans-list/timer/shared/TimerPanelShell'
import { isSipStrategyInProgress, shouldShowSipStrategyShell } from '../shared/planStatus'
import { PlansScreen } from '../../../../analytics/plansEvents'
import {
  getPlansFlowTheme,
  PlansThemeFlow
} from '../../../../lumpsum/plans-list/header/common/riskProfileHeaderTheme'
import { primeTimerCountdownChrome } from '../../../../lumpsum/plans-list/timer/shared/timerCountdownChrome'

const FLOW_THEME = getPlansFlowTheme(PlansThemeFlow.SIP_YOUR_PLAN)

type SipYourPlanTimerPanelProps = {
  planStatus?: SipPlanStatus | null
  timeToPlanDrop?: number | null
  isSubscriptionEligible: boolean
  strategyCta?: unknown
  refetchPlan: () => void
}

/** Strategy / timer / delayed shell before PLAN_ROLLED_OUT (Your Plan tab). */
export function SipYourPlanTimerPanel({
  planStatus,
  timeToPlanDrop,
  isSubscriptionEligible,
  strategyCta,
  refetchPlan
}: SipYourPlanTimerPanelProps) {
  const showStrategy = shouldShowSipStrategyShell(planStatus)
  const isDelayed = !showStrategy && isPlanDropDelayed(timeToPlanDrop)
  const countdownParts = usePlanCountdown(showStrategy || isDelayed ? 0 : timeToPlanDrop)

  return (
    <TimerPanelShell
      background={FLOW_THEME.chromeBackground}
      title="SIP Plan"
      description="Know which SIPs to start, increase, decrease or stop so your portfolio's allocation stays on the right track."
      hero={
        showStrategy ? (
          <TimerPanelStrategySection
            promptClassName={FLOW_THEME.primaryDarkTextClassName}
            variant={isSipStrategyInProgress(planStatus) ? 'complete' : 'find'}
            strategyCta={strategyCta}
            refetchPlan={refetchPlan}
            analyticsScreen={PlansScreen.SipYourPlanTimer}
          />
        ) : (
          <TimerPanelCountdownSection
            {...primeTimerCountdownChrome(FLOW_THEME, {
              readyIn: 'Portfolio under review, your plan will be ready in',
              delayTitle: 'Your SIP plan is delayed',
              delayBody:
                "We need extra time to prepare your SIP plan. We'll update you as soon as it's ready.",
              unlockPrompt: "Unlock your SIP plan once it's ready"
            })}
            countdownParts={countdownParts}
            isDelayed={isDelayed}
            isSubscriptionEligible={isSubscriptionEligible}
            unlockButtonLabel="Unlock with Prime"
            landingPage="prime"
            analyticsScreen={PlansScreen.SipYourPlanTimer}
          />
        )
      }
      footer={
        <>
          <TimerPanelFooterHeading>An SIP portfolio shaped by your allocation</TimerPanelFooterHeading>
          <TimerPanelFooterCopy>
            Our experts review your SIP contributions to ensure that your portfolio stays aligned with
            your allocation strategy. Here&apos;s what we focus on:
          </TimerPanelFooterCopy>
          <TimerPanelFeatureRows rows={SIP_YOUR_PLAN_FEATURE_ROWS} iconColor={FLOW_THEME.primary} />
        </>
      }
    />
  )
}
